// client/src/components/bank/OpenRequests.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDuration, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const OpenRequests = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { type } = useParams();
  const isHotMoney = type === 'hot-money';
  const hasTypeFilter = !!type;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();


  const formatRateTypesDisplay = (request) => {
    // Handle new rateTypes array format - check if it exists AND has elements
    if (request.rateTypes && Array.isArray(request.rateTypes) && request.rateTypes.length > 0) {
      // Sort alphabetically: 'fixed' comes before 'floating'
      const sortedTypes = [...request.rateTypes].sort();
      return sortedTypes.map(type => 
        type === 'fixed' ? t('requests.fixed') : t('requests.floating')
      ).join(', ');
    }
    
    // Fallback to old rateType field for backward compatibility
    if (request.rateType) {
      return request.rateType === 'floating' ? t('requests.floating') : t('requests.fixed');
    }
    
    return '';
  };
    
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all open requests
        const openRequestsRes = await axios.get('/api/requests?status=bidding');
               
        // Fetch all user's bids to check which requests already have bids
        const userBidsRes = await axios.get('/api/bids/bank');
        
        // Filter out bids with null requests
        const validBids = userBidsRes.data.bids.filter(bid => bid.request !== null);
        
        // Filter out requests that the user has already bid on with ACTIVE bids
        const requestsWithoutActiveBids = openRequestsRes.data.requests.filter(request => {
          // Check if the user has any ACTIVE bids for this request
          // Only consider pending, approved bids (not cancelled)
          const hasActiveBidForRequest = validBids.some(bid =>
            bid.request?.requestId === request.requestId &&
            bid.status !== 'cancelled'
          );

          // Only include requests that don't have active bids
          return !hasActiveBidForRequest;
        });

        // Filter by request type when accessed via a type-specific route
        const typedRequests = hasTypeFilter
          ? requestsWithoutActiveBids.filter(request => isHotMoney
              ? request.requestType === 'hot_money'
              : (!request.requestType || request.requestType === 'allocation'))
          : requestsWithoutActiveBids;

        setRequests(typedRequests);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching open requests:', err);
        setError(t('common.errors.fetchRequests') + ': ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };

    fetchData();
  }, [t, hasTypeFilter, isHotMoney]);
  
  const openBidForm = (requestId) => {
    // Navigate to bid form
    navigate(`/bank/submit-bid/${requestId}`);
  };

  // Mobile Card Component
  const MobileRequestCard = ({ request }) => (
    <div className="mobile-record-card">
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          #{request.requestId}
        </div>
        <div className="mobile-card-status" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {request.isRenewal && (
            <span className="status" style={{ backgroundColor: '#e6f4ee', color: '#0f8a5f' }}>
              {t('requests.renewal')}
            </span>
          )}
          <span className="status status-open">{t('status.open')}</span>
        </div>
      </div>

      <div className="mobile-card-body">
        {!hasTypeFilter && (
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.type')}</span>
            <span className="mobile-card-value">
              <span className="status" style={request.requestType === 'hot_money'
                ? { backgroundColor: '#f8dfb0', color: '#8a5300' }
                : { backgroundColor: '#cfe3fb', color: '#0d47a1' }}>
                {request.requestType === 'hot_money' ? t('requests.hotMoney') : t('requests.allocation')}
              </span>
            </span>
          </div>
        )}
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.company')}</span>
          <span className="mobile-card-value">{request.company?.name || t('bids.notAvailable')}</span>
        </div>
        
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.rateTypes')}</span>
          <span className="mobile-card-value">
            {formatRateTypesDisplay(request)}
          </span>
        </div>
        
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.maturity')}</span>
          <span className="mobile-card-value">
            {formatDuration(request.duration, t)}
          </span>
        </div>
        
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.bidDeadline')}</span>
          <span className="mobile-card-value">
            {new Date(request.bidDeadline).toLocaleDateString()}
            {' '}
            <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
              {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <span style={{ fontSize: '0.8em' }}>CET</span>
            </span>
          </span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-date">
          {/* Spazio per eventuali info aggiuntive */}
        </div>
        <div className="mobile-card-action">
          <button
            onClick={() => openBidForm(request.id)}
            className="view-btn"
          >
            {t('bids.submitBid')}
          </button>
        </div>
      </div>
    </div>
  );
  
  if (loading) {
    return <div className="loading">{t('common.loadingRequests')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="open-requests company-dashboard">
      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <Plus className="card-title-icon" size={20} />
          <span>
            {hasTypeFilter
              ? `${t('requests.newAvailableRequests')} - ${isHotMoney ? t('requests.hotMoney') : t('requests.allocation')}`
              : t('requests.newAvailableRequests')}
          </span>
        </div>

        {/* Desktop Table */}
        {!isMobile && (
          <table style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: '12%' }}>{t('requests.requestId')}</th>
                <th style={{ width: '12%' }}>{t('requests.company')}</th>
                <th style={{ width: '14%' }}>{t('requests.amount')}</th>
                <th style={{ width: '12%' }}>{t('requests.rateType')}</th>
                <th style={{ width: '11%' }}>{t('requests.maturity')}</th>
                <th style={{ width: '14%' }}>{t('requests.bidDeadline')}</th>
                {!hasTypeFilter && <th style={{ width: '10%' }}>{t('requests.type')}</th>}
                <th style={{ width: '10%' }}>{t('bids.status')}</th>
                <th style={{ width: '15%' }}>{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {requests.length > 0 ? (
                requests.map(request => (
                  <tr key={request.id}>
                    <td>#{request.requestId}</td>
                    <td>{request.company?.name || t('bids.notAvailable')}</td>
                    <td>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</td>
                    <td>{formatRateTypesDisplay(request)}</td>
                    <td>
                      {formatDuration(request.duration, t)}
                    </td>
                    <td>
                      {new Date(request.bidDeadline).toLocaleDateString()}
                      {' '}
                      <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                        {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <span style={{ fontSize: '0.8em' }}>CET</span>
                      </span>
                    </td>
                    {!hasTypeFilter && (
                      <td>
                        <span className="status" style={request.requestType === 'hot_money'
                          ? { backgroundColor: '#f8dfb0', color: '#8a5300', fontSize: '10px', padding: '3px 7px', lineHeight: '1.4' }
                          : { backgroundColor: '#cfe3fb', color: '#0d47a1', fontSize: '10px', padding: '3px 7px', lineHeight: '1.4' }}>
                          {request.requestType === 'hot_money' ? t('requests.hotMoney') : t('requests.allocation')}
                        </span>
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {request.isRenewal && (
                          <span className="status" style={{ backgroundColor: '#e6f4ee', color: '#0f8a5f', fontSize: '10px', padding: '3px 7px', lineHeight: '1.4' }}>
                            {t('requests.renewal')}
                          </span>
                        )}
                        <span className="status status-open" style={{ fontSize: '10px', padding: '3px 7px', lineHeight: '1.4', border: '1px solid transparent' }}>{t('status.open')}</span>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => openBidForm(request.id)}
                        className="view-btn"
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px'
                        }}
                      >
                        {t('bids.submitBid')}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={hasTypeFilter ? 8 : 9}>{t('requests.noNewRequests')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Mobile Cards - Solo su mobile */}
        {isMobile && (
          <div className="mobile-cards-container">
            {requests.length > 0 ? (
              requests.map(request => (
                <MobileRequestCard key={request.id} request={request} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('requests.noNewRequests')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenRequests;